import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { ShiftAuthRequestError } from '../../services/auth/shift-auth.service';
import { shiftHomeService } from '../../services/home/shift-home.service';
import type { ShiftHomeDashboard } from '../../models/home.types';
import { resolveRequestErrorMessage } from '../../utils/request-errors.utils';

export function useHomePageController() {
  const navigate = useNavigate();

  const { user } = useShiftAuth();

  const [dashboard, setDashboard] = useState<ShiftHomeDashboard | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [announcementDraft, setAnnouncementDraft] = useState('');
  const [isEditingAnnouncement, setEditingAnnouncement] = useState(false);
  const [isSavingAnnouncement, setIsSavingAnnouncement] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const announcementSaveLock = useRef(false);
  const editing = useRef(false);
  const editingBaseline = useRef('');
  const canonicalAnnouncement = useRef('');
  const announcementRevision = useRef(0);
  const generation = useRef(0);
  const active = useRef(true);
  const scope = `${user?.tenantPk}:${user?.id}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const liveDashboard = useRef<ShiftHomeDashboard | null>(null);
  const liveAnnouncement = useRef<string | null>(null);

  const applyAnnouncement = (value: string) => {
    canonicalAnnouncement.current = value;
    setAnnouncement(value);
    if (!editing.current && !announcementSaveLock.current) setAnnouncementDraft(value);
  };

  const setIsEditingAnnouncement = (value: boolean) => {
    editing.current = value;
    if (value) editingBaseline.current = canonicalAnnouncement.current;
    else setAnnouncementDraft(canonicalAnnouncement.current);
    setEditingAnnouncement(value);
  };

  const loadDashboard = async () => {
    const requestGeneration = ++generation.current;
    liveDashboard.current = null;
    liveAnnouncement.current = null;
    setIsLoading(true);
    setError(null);

    try {
      const [dashboardPayload, announcementPayload] = await Promise.all([
        shiftHomeService.getDashboard(),
        shiftHomeService.getManagerAnnouncement(),
      ]);
      if (requestGeneration !== generation.current || scope !== currentScope.current) return;
      setDashboard(liveDashboard.current ?? dashboardPayload);
      applyAnnouncement(liveAnnouncement.current ?? announcementPayload.announcement);
    } catch (requestError: unknown) {
      if (requestGeneration !== generation.current || scope !== currentScope.current) return;
      if (requestError instanceof ShiftAuthRequestError) {
        if (requestError.isNetworkError) {
          setError('אין תקשורת לשרת. נסו שוב בעוד רגע.');
        } else {
          setError('לא ניתן לטעון את הסידור כרגע.');
        }
      } else {
        setError('לא ניתן לטעון את הסידור כרגע.');
      }
    } finally {
      if (requestGeneration === generation.current && scope === currentScope.current) setIsLoading(false);
    }
  };

  const eventsReady = useShiftEvents((event) => {
    if (event.type === 'home.assignmentsChanged') {
      liveDashboard.current = event.dashboard;
      setDashboard(event.dashboard);
    } else if (event.type === 'home.announcementChanged') {
      announcementRevision.current += 1;
      liveAnnouncement.current = event.announcement.announcement;
      applyAnnouncement(event.announcement.announcement);
    } else if (event.type === 'sse.resync') void loadDashboard();
  });

  useEffect(() => {
    active.current = true;
    let start = true;
    void Promise.resolve().then(() => { if (start && eventsReady) void loadDashboard(); });
    return () => {
      start = false;
      active.current = false;
      generation.current += 1;
    };
  }, [eventsReady, scope]);

  const onSaveAnnouncement = async () => {
    if (announcementSaveLock.current) return;
    if (announcementDraft === (editing.current ? editingBaseline.current : canonicalAnnouncement.current)) {
      setIsEditingAnnouncement(false);
      return;
    }
    announcementSaveLock.current = true;
    const revision = announcementRevision.current;
    setIsSavingAnnouncement(true);
    try {
      const saved = await shiftHomeService.updateManagerAnnouncement(announcementDraft);
      if (!active.current || scope !== currentScope.current) return;
      if (revision === announcementRevision.current) {
        liveAnnouncement.current = saved.announcement;
        applyAnnouncement(saved.announcement);
      }
      setIsEditingAnnouncement(false);
      notifications.show({ color: 'green', message: 'הודעת המנהל נשמרה בהצלחה.' });
    } catch (requestError) {
      if (!active.current || scope !== currentScope.current) return;
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: resolveRequestErrorMessage(requestError, 'שמירת הודעת המנהל נכשלה.'),
      });
    } finally {
      announcementSaveLock.current = false;
      if (active.current && scope === currentScope.current) setIsSavingAnnouncement(false);
    }
  };

  return {
    navigate,
    user,
    dashboard,
    announcement,
    announcementDraft,
    setAnnouncementDraft,
    isEditingAnnouncement,
    setIsEditingAnnouncement,
    isSavingAnnouncement,
    isLoading,
    error,
    loadDashboard,
    onSaveAnnouncement,
  };
}
